import type { ReactElement, SVGProps } from 'react';
import { cx } from '../lib/cx.ts';

// MOODS icon family — 24px grid, 1.75 stroke, round caps/joins, 2px corner radius language.
// Each entry is a list of SVG primitives. Strings are path data; objects are {c:[cx,cy,r], f:true} (f = filled dot).

/** Circle `[cx, cy, r]`; `f` renders it as a filled dot instead of a stroked ring. */
export interface IconCircle {
  c: [number, number, number];
  f?: boolean;
}
/** Rounded rect `[x, y, width, height, rx]`. */
export interface IconRect {
  r: [number, number, number, number, number];
}
/** Path data `p` drawn with an SVG `transform` `t`. */
export interface IconTransformedPath {
  p: string;
  t: string;
}
/** One drawing primitive of an icon: a bare string is path data. */
export type IconPrimitive = string | IconCircle | IconRect | IconTransformedPath;

const D = (x: number, y: number, r = 1.15): IconCircle => ({ c: [x, y, r], f: true });
const slash = 'M4.5 4.5l15 15';
function gear(): string {
  const pts: Array<[number, number]> = [];
  const n = 8,
    ro = 9.2,
    ri = 7.1;
  for (let i = 0; i < n * 4; i++) {
    const a = (i / (n * 4)) * Math.PI * 2 - Math.PI / 2 + Math.PI / (n * 4);
    const r = i % 4 < 2 ? ro : ri;
    pts.push([12 + r * Math.cos(a), 12 + r * Math.sin(a)]);
  }
  return 'M' + pts.map(p => p[0].toFixed(2) + ' ' + p[1].toFixed(2)).join('L') + 'Z';
}
function burst(n: number, ro: number, ri: number, cy = 12): string {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < n * 2; i++) {
    const a = (i / (n * 2)) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 ? ri : ro;
    pts.push([12 + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return 'M' + pts.map(p => p[0].toFixed(2) + ' ' + p[1].toFixed(2)).join('L') + 'Z';
}
const phone =
  'M8.6 4.5H6.1a1.5 1.5 0 0 0-1.5 1.63c.62 7.12 6.15 12.65 13.27 13.27A1.5 1.5 0 0 0 19.5 17.9v-2.5l-3.7-1.6-1.85 1.85a10.7 10.7 0 0 1-5.8-5.8L10 8.2z';
const shield = 'M12 3.75 19 6.5v5.1c0 4.3-2.9 7.55-7 8.65-4.1-1.1-7-4.35-7-8.65V6.5z';
const mic: IconPrimitive[] = [{ r: [9, 3.5, 6, 11, 3] }, 'M5.5 11.5a6.5 6.5 0 0 0 13 0', 'M12 18v2.5'];
const headphones: IconPrimitive[] = [
  'M4.5 15.5v-3a7.5 7.5 0 0 1 15 0v3',
  { r: [4.5, 14, 4, 6.5, 1.75] },
  { r: [15.5, 14, 4, 6.5, 1.75] },
];
const camera: IconPrimitive[] = [{ r: [3.5, 6.5, 12, 11, 2.5] }, 'M15.5 10.6l5-2.9v8.6l-5-2.9'];
const speaker = 'M4.5 9.5h3l4.5-4v13l-4.5-4h-3a.5.5 0 0 1-.5-.5v-4a.5.5 0 0 1 .5-.5z';
const bubble = 'M6 5h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-6.5L7 20.5V17H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z';
const bell: IconPrimitive[] = [
  'M6.5 16.5V11a5.5 5.5 0 0 1 11 0v5.5l1.5 1.75h-14z',
  'M10 20.5a2.1 2.1 0 0 0 4 0',
];
const pin: IconPrimitive[] = ['M9 4.5h6l-.9 5.4 2.9 3.1H7l2.9-3.1z', 'M12 13v7'];
const person: IconPrimitive[] = [{ c: [12, 8.5, 3.75] }, 'M5 20c.8-3.5 3.6-5.5 7-5.5s6.2 2 7 5.5'];
const emoji: IconPrimitive[] = [
  { c: [12, 12, 8.5] },
  'M8.75 14.25a4 4 0 0 0 6.5 0',
  D(9.25, 10),
  D(14.75, 10),
];

export const ICONS = {
  home: ['M4 10.4 12 4l8 6.4V18.5a1.5 1.5 0 0 1-1.5 1.5H15v-5.5H9V20H5.5A1.5 1.5 0 0 1 4 18.5z'],
  friends: [
    { c: [9, 8.5, 3.25] },
    'M3.25 19c.6-3 2.9-4.75 5.75-4.75S14.15 16 14.75 19',
    { c: [16.75, 9.5, 2.5] },
    'M16.5 14.3c2.2.15 3.85 1.6 4.25 4.2',
  ],
  messages: [bubble],
  chat: [bubble, 'M8 9.25h8M8 12.75h5'],
  communities: [
    { r: [4, 4, 7, 7, 2] },
    { r: [13, 4, 7, 7, 2] },
    { r: [4, 13, 7, 7, 2] },
    { c: [16.5, 16.5, 3.5] },
  ],
  discover: [{ c: [12, 12, 8.5] }, 'M15 9l-1.9 4.1L9 15l1.9-4.1z'],
  plus: ['M12 5v14M5 12h14'],
  'add-community': [{ r: [3.5, 3.5, 17, 17, 5] }, 'M12 8.5v7M8.5 12h7'],
  search: [{ c: [11, 11, 6.5] }, 'M16 16l4 4'],
  bell,
  'bell-off': [...bell, slash],
  inbox: [
    'M4 13.5 6.2 6.4A1.5 1.5 0 0 1 7.6 5.3h8.8a1.5 1.5 0 0 1 1.4 1.1L20 13.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18z',
    'M4 13.5h4.5l1.25 2h4.5l1.25-2H20',
  ],
  help: [{ c: [12, 12, 8.5] }, 'M9.75 9.6a2.3 2.3 0 0 1 4.5.6c0 1.6-2.25 2-2.25 3.4', D(12, 16.8)],
  settings: [gear(), { c: [12, 12, 2.75] }],
  sliders: ['M5 7h8M17 7h2M5 17h2M11 17h8', { c: [15, 7, 2] }, { c: [9, 17, 2] }],
  more: [D(6, 12, 1.4), D(12, 12, 1.4), D(18, 12, 1.4)],
  'more-v': [D(12, 6, 1.4), D(12, 12, 1.4), D(12, 18, 1.4)],
  close: ['M6.5 6.5l11 11M17.5 6.5l-11 11'],
  back: ['M14.5 6l-6 6 6 6'],
  forward: ['M9.5 6l6 6-6 6'],
  'chevron-down': ['M7 10l5 5 5-5'],
  'chevron-right': ['M10 7l5 5-5 5'],
  'chevron-up': ['M7 14l5-5 5 5'],
  menu: ['M4.5 7h15M4.5 12h15M4.5 17h15'],
  mic,
  'mic-off': [...mic, slash],
  headphones,
  deafen: [...headphones, slash],
  camera,
  'camera-off': [...camera, slash],
  'screen-share': [{ r: [3, 4.5, 18, 12.5, 2] }, 'M8.5 20.5h7', 'M12 13.5v-5M9.5 11 12 8.5l2.5 2.5'],
  stream: [
    { c: [12, 12, 2] },
    'M8.3 15.7a5.25 5.25 0 0 1 0-7.4',
    'M15.7 8.3a5.25 5.25 0 0 1 0 7.4',
    'M5.5 18.5a9.2 9.2 0 0 1 0-13',
    'M18.5 5.5a9.2 9.2 0 0 1 0 13',
  ],
  phone: [phone],
  'leave-call': [{ p: phone, t: 'rotate(135 12 12)' }],
  speaker: [speaker, 'M15.5 9.25a4 4 0 0 1 0 5.5', 'M18 6.75a7.5 7.5 0 0 1 0 10.5'],
  'speaker-off': [speaker, 'M16 9.75l4.5 4.5M20.5 9.75 16 14.25'],
  noise: ['M3.5 12h2l1.75-3.5 2.25 7 2.5-10 2.5 13 2.25-8.5 1.75 2H20.5'],
  connection: ['M5 18.5v-3', 'M9.5 18.5v-6', 'M14 18.5v-9', 'M18.5 18.5v-12'],
  wifi: ['M4.5 10a10.6 10.6 0 0 1 15 0', 'M7.4 13.1a6.5 6.5 0 0 1 9.2 0', D(12, 16.75, 1.3)],
  'text-channel': ['M10 4.5 8.25 19.5M15.75 4.5 14 19.5M5.25 9h14M4.75 15h14'],
  'voice-channel': [speaker, 'M15.75 9.5a3.5 3.5 0 0 1 0 5'],
  forum: [
    'M4 6.5A1.5 1.5 0 0 1 5.5 5h9A1.5 1.5 0 0 1 16 6.5v5a1.5 1.5 0 0 1-1.5 1.5H9.25L6 15.5V13h-.5A1.5 1.5 0 0 1 4 11.5z',
    'M18.5 9.5a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-1.5 1.5H18V20l-3.25-2.5H11.5',
  ],
  announcement: [
    'M4.5 10v4a1 1 0 0 0 1 1h2.25l7.75 4.5v-15L7.75 9H5.5a1 1 0 0 0-1 1z',
    'M8 15l1.25 4.5',
    'M18.5 9.5a3.2 3.2 0 0 1 0 5',
  ],
  stage: [
    'M12 4.5a2.5 2.5 0 0 1 2.5 2.5v3a2.5 2.5 0 0 1-5 0V7A2.5 2.5 0 0 1 12 4.5z',
    'M7 10a5 5 0 0 0 10 0',
    'M4.5 20h15M12 15v5',
  ],
  category: [
    'M4 7.5A1.5 1.5 0 0 1 5.5 6h4l2 2h7A1.5 1.5 0 0 1 20 9.5v8a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5z',
  ],
  lock: [{ r: [5.5, 10.5, 13, 9.5, 2] }, 'M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5'],
  'private-channel': [
    'M9.5 4.5 7.75 19.5M14.75 4.5l-.6 5M4.75 9h7.5M4.25 15h7',
    { r: [13.5, 15.25, 7, 5.25, 1.25] },
    'M15 15.25v-1.5a2 2 0 0 1 4 0v1.5',
  ],
  thread: ['M6.5 4.5V13a3 3 0 0 0 3 3h9', 'M15.5 13l3 3-3 3'],
  members: [
    { c: [12, 8, 3] },
    'M6.5 19a5.5 5.5 0 0 1 11 0',
    { c: [5.5, 10, 2] },
    'M2.75 16.5a3.4 3.4 0 0 1 3-2.4',
    { c: [18.5, 10, 2] },
    'M21.25 16.5a3.4 3.4 0 0 0-3-2.4',
  ],
  invite: [{ c: [10, 8.5, 3.25] }, 'M4 19c.6-3 2.9-4.75 6-4.75 1.2 0 2.3.3 3.2.8', 'M18 13.5v6M15 16.5h6'],
  roles: [
    'M4.5 5.5v5.1c0 .4.16.78.44 1.06l7.4 7.4a1.5 1.5 0 0 0 2.12 0l4.08-4.08a1.5 1.5 0 0 0 0-2.12l-7.4-7.4A1.5 1.5 0 0 0 10.1 5H5a.5.5 0 0 0-.5.5z',
    D(8.5, 8.75, 1.3),
  ],
  permissions: [shield, 'M9 10.25h6M9 13.75h4'],
  shield: [shield],
  moderator: [shield, 'M9 12l2 2 4-4'],
  admin: [shield, 'M12 7.75 15.25 9.1v2.4c0 2-1.35 3.5-3.25 4.1-1.9-.6-3.25-2.1-3.25-4.1V9.1z'],
  owner: ['M4.5 17.5 3.5 8l4.75 3.5L12 5.5l3.75 6L20.5 8l-1 9.5z', 'M5 20.25h14'],
  verified: [burst(10, 9, 7.4), 'M8.75 12.25l2.25 2.25 4.25-4.5'],
  bot: [
    { r: [5, 8, 14, 11, 3] },
    'M12 8V5.5',
    D(12, 4.5, 1.2),
    D(9.5, 12.75, 1.2),
    D(14.5, 12.75, 1.2),
    'M10 16h4',
  ],
  premium: ['M7 4.75h10l3.5 4.75L12 19.75 3.5 9.5z', 'M3.5 9.5h17', 'M9.5 4.75 12 19.75l2.5-15'],
  // MOD ekonomisi (owner 2026-10-23): a tall gem after the diamond at the heart of the MOODS symbol — new art on this
  // grid, never the symbol itself (docs/BRAND_KIT.md); ModGem is the bright, filled version
  mod: ['M12 3.25c2.85 3 4.6 5.9 4.6 8.75S14.85 17.75 12 20.75C9.15 17.75 7.4 14.85 7.4 12S9.15 6.25 12 3.25z', 'M12 8.4l1.9 3.6-1.9 3.6-1.9-3.6z'],
  emoji,
  gif: [
    { r: [3.5, 6, 17, 12, 2.5] },
    'M10 10.2a2 2 0 0 0-3.6 1.2v1.2a2 2 0 0 0 3.6 1.2V12H8.6',
    'M12.5 9.75v4.5',
    'M15 14.25v-4.5h2.75M15 12h2.25',
  ],
  sticker: [
    'M5.5 4.5h9l5 5V18a1.5 1.5 0 0 1-1.5 1.5H5.5A1.5 1.5 0 0 1 4 18V6a1.5 1.5 0 0 1 1.5-1.5z',
    'M14.5 4.5V8a1.5 1.5 0 0 0 1.5 1.5h3.5',
    'M8.5 14.25a3.5 3.5 0 0 0 5.5 0',
  ],
  apps: [
    { r: [4, 4, 6.5, 6.5, 2] },
    { r: [13.5, 4, 6.5, 6.5, 2] },
    { r: [4, 13.5, 6.5, 6.5, 2] },
    'M16.75 13.5v6.5M13.5 16.75H20',
  ],
  upload: [
    'M12 15V4.5M7.5 9 12 4.5 16.5 9',
    'M4.5 14.5v3.5A1.5 1.5 0 0 0 6 19.5h12a1.5 1.5 0 0 0 1.5-1.5v-3.5',
  ],
  download: [
    'M12 4.5V15M7.5 10.5 12 15l4.5-4.5',
    'M4.5 14.5v3.5A1.5 1.5 0 0 0 6 19.5h12a1.5 1.5 0 0 0 1.5-1.5v-3.5',
  ],
  gift: [
    { r: [4, 8.75, 16, 4.25, 1.25] },
    'M5.5 13v5.5a1.5 1.5 0 0 0 1.5 1.5h10a1.5 1.5 0 0 0 1.5-1.5V13',
    'M12 8.75V20',
    'M12 8.75C10.75 6 7.75 5.4 7.75 7.4 7.75 8.6 9.5 8.75 12 8.75zM12 8.75c1.25-2.75 4.25-3.35 4.25-1.35 0 1.2-1.75 1.35-4.25 1.35z',
  ],
  reaction: [
    { c: [11, 13, 7.5] },
    'M8 14.75a3.6 3.6 0 0 0 6 0',
    D(8.6, 11.25, 1.05),
    D(13.4, 11.25, 1.05),
    'M19 2.75v5M16.5 5.25h5',
  ],
  reply: ['M9.5 6.5 4.5 11.5l5 5', 'M4.5 11.5h9a6 6 0 0 1 6 6v1'],
  edit: ['M15.5 5 19 8.5 9 18.5l-4.5 1 1-4.5z', 'M13.5 7l3.5 3.5'],
  delete: [
    'M4.5 7h15',
    'M9.5 7V5.5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1V7',
    'M6.5 7l.8 11.6A1.5 1.5 0 0 0 8.8 20h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7',
    'M10.25 11v5M13.75 11v5',
  ],
  pin,
  unpin: [...pin, slash],
  copy: [
    { r: [8.5, 8.5, 11, 11, 2] },
    'M15.5 5.5V5A1.5 1.5 0 0 0 14 3.5H6A1.5 1.5 0 0 0 4.5 5v8A1.5 1.5 0 0 0 6 14.5h.5',
  ],
  mention: [{ c: [12, 12, 3.25] }, 'M15.25 12v1.5a2.25 2.25 0 0 0 4.5 0V12a7.75 7.75 0 1 0-3.2 6.3'],
  block: [{ c: [12, 12, 8.5] }, 'M6 18 18 6'],
  report: ['M5.5 20.5V4.5', 'M5.5 5h11.5l-2.4 4.25L17 13.5H5.5'],
  profile: person,
  activity: ['M3.5 12h4l2.5-6 4 12 2.5-6h4'],
  gamepad: [
    'M7.5 7.5h9a4.5 4.5 0 0 1 4.4 5.5l-.9 4a2.4 2.4 0 0 1-4.1 1.1L14 16h-4l-1.9 2.1A2.4 2.4 0 0 1 4 17l-.9-4a4.5 4.5 0 0 1 4.4-5.5z',
    'M8 10.25v3.5M6.25 12h3.5',
    D(15.25, 11, 1.1),
    D(17.25, 13, 1.1),
  ],
  music: ['M9 17.5V6.25l10-2v11.5', { c: [7, 17.5, 2] }, { c: [17, 15.75, 2] }],
  link: [
    'M10 14a3.5 3.5 0 0 0 5 0l3-3a3.5 3.5 0 0 0-5-5l-.75.75',
    'M14 10a3.5 3.5 0 0 0-5 0l-3 3a3.5 3.5 0 0 0 5 5l.75-.75',
  ],
  external: [
    'M13.5 4.5h6v6',
    'M19.5 4.5 11 13',
    'M17.5 14v4a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 18V8A1.5 1.5 0 0 1 6 6.5h4',
  ],
  calendar: [{ r: [4, 5.5, 16, 14.5, 2.5] }, 'M4 10h16M8.5 3.5v4M15.5 3.5v4'],
  clock: [{ c: [12, 12, 8.5] }, 'M12 7.5V12l3 2'],
  file: [
    'M6.5 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 5 19V5a1.5 1.5 0 0 1 1.5-1.5z',
    'M13.5 3.5V8H18',
  ],
  image: [{ r: [3.5, 4.5, 17, 15, 2.5] }, { c: [9, 9.5, 1.75] }, 'M20.5 15.5 15.5 11 6 19.5'],
  video: [{ r: [3.5, 5, 17, 14, 2.5] }, 'M10.5 9.5v5l4-2.5z'],
  voice: ['M4 11v2M7.5 8.5v7M11 5.5v13M14.5 9v6M18 7.5v9M21 11v2'],
  code: ['M8.5 8 4.5 12l4 4M15.5 8l4 4-4 4', 'M13.5 5.5l-3 13'],
  poll: ['M5 6.5h8M5 12h14M5 17.5h5'],
  save: ['M7 4.5h10a1 1 0 0 1 1 1v14l-6-4-6 4v-14a1 1 0 0 1 1-1z'],
  share: [
    { c: [6.5, 12, 2.25] },
    { c: [17, 6.5, 2.25] },
    { c: [17, 17.5, 2.25] },
    'M8.5 11 15 7.6M8.5 13 15 16.4',
  ],
  star: [burst(5, 8.5, 3.9, 12.6)],
  filter: ['M4.5 6.5h15M7.5 12h9M10.5 17.5h3'],
  grid: [
    { r: [4, 4, 7, 7, 1.75] },
    { r: [13, 4, 7, 7, 1.75] },
    { r: [4, 13, 7, 7, 1.75] },
    { r: [13, 13, 7, 7, 1.75] },
  ],
  list: ['M9 6.5h11M9 12h11M9 17.5h11', D(5, 6.5), D(5, 12), D(5, 17.5)],
  check: ['M5 12.5l4.5 4.5L19 7.5'],
  info: [{ c: [12, 12, 8.5] }, 'M12 11v5.5', D(12, 8)],
  warning: [
    'M10.7 5.2a1.5 1.5 0 0 1 2.6 0l7 12.3a1.5 1.5 0 0 1-1.3 2.25H5a1.5 1.5 0 0 1-1.3-2.25z',
    'M12 10v4',
    D(12, 16.6),
  ],
  fullscreen: [
    'M4.5 9V5.5a1 1 0 0 1 1-1H9M15 4.5h3.5a1 1 0 0 1 1 1V9M19.5 15v3.5a1 1 0 0 1-1 1H15M9 19.5H5.5a1 1 0 0 1-1-1V15',
  ],
  popout: [{ r: [3.5, 5, 17, 14, 2.5] }, { r: [11.5, 11.5, 6, 4.5, 1] }],
  flame: [
    'M12 20.5a6 6 0 0 0 6-6c0-4-3-5.5-3.5-9.5-2.5 1.5-4 4-4 6.5-1-.5-1.8-1.6-2-2.8C7 10 6 12 6 14.5a6 6 0 0 0 6 6z',
  ],
  trophy: [
    'M8 4.5h8v5a4 4 0 0 1-8 0z',
    'M8 6.5H5.5V8a3 3 0 0 0 3 3',
    'M16 6.5h2.5V8a3 3 0 0 1-3 3',
    'M12 13.5v3.5M8.5 20h7',
  ],
  film: [{ r: [3.5, 5, 17, 14, 2.5] }, 'M7.5 5v14M16.5 5v14M3.5 9.5h4M3.5 14.5h4M16.5 9.5h4M16.5 14.5h4'],
  // Sinema Salonu (2026-10-24): play / pause in the family's line style (24 grid, 1.75 stroke, round caps and joins)
  play: ['M8.5 5.6v12.8a.9.9 0 0 0 1.38.76l9.96-6.4a.9.9 0 0 0 0-1.52L9.88 4.84a.9.9 0 0 0-1.38.76z'],
  pause: ['M9 5.5v13M15 5.5v13'],
  globe: [
    { c: [12, 12, 8.5] },
    'M3.5 12h17',
    'M12 3.5c2.4 2.6 3.5 5.5 3.5 8.5s-1.1 5.9-3.5 8.5c-2.4-2.6-3.5-5.5-3.5-8.5S9.6 6.1 12 3.5z',
  ],
  smartphone: [{ r: [7, 3.5, 10, 17, 2.5] }, 'M11 17.25h2'],
  monitor: [{ r: [3, 4.5, 18, 12.5, 2] }, 'M8.5 20.5h7M12 17v3.5'],
  keyboard: [
    { r: [3, 6, 18, 12, 2.5] },
    D(7, 10, 1),
    D(10.33, 10, 1),
    D(13.66, 10, 1),
    D(17, 10, 1),
    'M8 14.25h8',
  ],
  palette: [
    'M12 3.5a8.5 8.5 0 0 0 0 17c1.1 0 1.6-.8 1.3-1.7-.4-1.2.3-2.3 1.6-2.3h1.6a3.5 3.5 0 0 0 3.5-3.5A8.5 8.5 0 0 0 12 3.5z',
    D(8, 11, 1.2),
    D(10.5, 7.75, 1.2),
    D(14.75, 8.25, 1.2),
  ],
  eye: [
    'M2.75 12S6 5.75 12 5.75 21.25 12 21.25 12 18 18.25 12 18.25 2.75 12 2.75 12z',
    { c: [12, 12, 2.75] },
  ],
  moon: ['M19.5 14.2A8 8 0 1 1 9.8 4.5a6.5 6.5 0 0 0 9.7 9.7z'],
  sun: [
    { c: [12, 12, 3.75] },
    'M12 3v1.75M12 19.25V21M3 12h1.75M19.25 12H21M5.6 5.6l1.25 1.25M17.15 17.15l1.25 1.25M5.6 18.4l1.25-1.25M17.15 6.85l1.25-1.25',
  ],
  refresh: [
    'M19.5 12a7.5 7.5 0 0 1-13.3 4.75M4.5 12a7.5 7.5 0 0 1 13.3-4.75',
    'M18 3.75v3.75h-3.75M6 20.25V16.5h3.75',
  ],
  logout: ['M14 4.5h4a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5h-4', 'M10 8l-4 4 4 4', 'M6 12h9.5'],
  minimize: ['M6.5 12h11'],
  maximize: [{ r: [6.5, 6.5, 11, 11, 1.5] }],
  sparkle: [
    'M12 4c.6 3.9 2.1 5.4 6 6-3.9.6-5.4 2.1-6 6-.6-3.9-2.1-5.4-6-6 3.9-.6 5.4-2.1 6-6z',
    'M18.5 15.5c.25 1.6.9 2.25 2.5 2.5-1.6.25-2.25.9-2.5 2.5-.25-1.6-.9-2.25-2.5-2.5 1.6-.25 2.25-.9 2.5-2.5z',
  ],
  'panel-right': [{ r: [3.5, 4.5, 17, 15, 2.5] }, 'M14.5 4.5v15'],
  'arrow-down': ['M12 5v14M6.5 13.5 12 19l5.5-5.5'],
  bold: ['M7.5 5h5.25a3.25 3.25 0 0 1 0 6.5H7.5zM7.5 11.5h6a3.5 3.5 0 0 1 0 7h-6z'],
  italic: ['M10 5h7M7 19h7M14 5l-4 14'],
  strike: [
    'M5 12h14',
    'M16.5 7.5c-.6-1.6-2.3-2.75-4.5-2.75-2.6 0-4.5 1.4-4.5 3.4 0 1.1.5 1.9 1.4 2.5',
    'M7.5 16.5c.6 1.6 2.3 2.75 4.5 2.75 2.6 0 4.5-1.4 4.5-3.4 0-.6-.15-1.1-.4-1.6',
  ],
  quote: ['M5 7.5v9', 'M9.5 9h9.5M9.5 15h6'],
  send: ['M4.5 12 19.5 4.5l-3.75 15-4.5-6z', 'M11.25 13.5 19.5 4.5'],
  location: ['M12 20.5s6.5-5.6 6.5-11a6.5 6.5 0 0 0-13 0c0 5.4 6.5 11 6.5 11z', { c: [12, 9.5, 2.25] }],
} satisfies Record<string, IconPrimitive[]>;

export type IconName = keyof typeof ICONS;
export const ICON_NAMES = Object.keys(ICONS) as IconName[];

/** Own-property lookup, so names like `constructor` resolve to the fallback instead of a prototype member. */
function iconDef(name: string): readonly IconPrimitive[] | undefined {
  return Object.hasOwn(ICONS, name) ? (ICONS as Record<string, IconPrimitive[]>)[name] : undefined;
}

function prim(p: IconPrimitive, i: number): ReactElement {
  if (typeof p === 'string') return <path key={i} d={p} />;
  if ('p' in p) return <path key={i} d={p.p} transform={p.t} />;
  if ('c' in p)
    return (
      <circle
        key={i}
        cx={p.c[0]}
        cy={p.c[1]}
        r={p.c[2]}
        fill={p.f ? 'currentColor' : 'none'}
        stroke={p.f ? 'none' : undefined}
      />
    );
  return <rect key={i} x={p.r[0]} y={p.r[1]} width={p.r[2]} height={p.r[3]} rx={p.r[4]} />;
}

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'stroke'> {
  /** Icon name; unknown names render the `help` icon. */
  name: IconName | (string & {});
  /** Width and height in px (default 20). */
  size?: number;
  /** Stroke width (default 1.75). */
  stroke?: number;
  /** Accessible label; without it the icon is `aria-hidden`. */
  title?: string;
}

export function Icon({ name, size = 20, stroke = 1.75, className, style, title, ...rest }: IconProps) {
  const def = iconDef(name) ?? ICONS.help;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx('md-icon', className)}
      style={style}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {def.map((p, i) => prim(p, i))}
    </svg>
  );
}

// Serialize an icon to a standalone SVG string (asset export).
export function iconSvg(name: IconName, color = 'currentColor', size = 24): string {
  const def: readonly IconPrimitive[] = ICONS[name];
  const body = def
    .map(p => {
      if (typeof p === 'string') return `<path d="${p}"/>`;
      if ('p' in p) return `<path d="${p.p}" transform="${p.t}"/>`;
      if ('c' in p)
        return p.f
          ? `<circle cx="${p.c[0]}" cy="${p.c[1]}" r="${p.c[2]}" fill="${color}" stroke="none"/>`
          : `<circle cx="${p.c[0]}" cy="${p.c[1]}" r="${p.c[2]}"/>`;
      return `<rect x="${p.r[0]}" y="${p.r[1]}" width="${p.r[2]}" height="${p.r[3]}" rx="${p.r[4]}"/>`;
    })
    .join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
}
